import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { translations } from '../translations';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CurrencySwitcher from '../components/CurrencySwitcher';
import Footer from '../components/Footer';
import './InvoicePage.css';

const InvoicePage = () => {
  const { language } = useLanguage();
  const { formatPrice, getCurrencySymbol, convertPrice, formatPriceOnly } = useCurrency();
  const t = translations[language];
  const navigate = useNavigate();
  
  const [bookingData, setBookingData] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: '',
    district: '',
    province: '',
    postalCode: '',
    country: 'Thailand',
    notes: ''
  });
  
  const [formValid, setFormValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addressSaved, setAddressSaved] = useState(false);
  const [deliveryCost] = useState(100); // Фиксированная стоимость доставки 30 THB
  const [orderId] = useState(() => 'TB-' + Date.now().toString().slice(-8) + '-' + Math.random().toString(36).substr(2, 4).toUpperCase());

  // Проверка валидности всех полей
  const checkFormValidity = () => {
    const requiredFields = ['street', 'city', 'province', 'postalCode'];
    const allRequiredFilled = requiredFields.every(field => 
      deliveryAddress[field] && deliveryAddress[field].trim() !== ''
    );
    setFormValid(allRequiredFilled);
  };

  useEffect(() => {
    const savedData = localStorage.getItem('bookingData');
    if (!savedData) {
      navigate('/');
      return;
    }
    try {
      const parsedData = JSON.parse(savedData);
      setBookingData(parsedData);
      
      // Загружаем сохраненный адрес если есть
      const savedAddress = localStorage.getItem('deliveryAddress');
      if (savedAddress) {
        const parsedAddress = JSON.parse(savedAddress);
        setDeliveryAddress(parsedAddress);
        setAddressSaved(true);
      }
    } catch (error) {
      console.error('Error parsing booking data:', error);
      navigate('/');
    }
  }, [navigate]);

  // Проверяем валидность формы при изменении адреса
  useEffect(() => {
    checkFormValidity();
  }, [deliveryAddress]);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setDeliveryAddress(prev => ({
      ...prev,
      [name]: value
    }));
    if (addressSaved) {
      setAddressSaved(false);
    }
  };

  const saveAddress = async () => {
    if (!formValid) {
      alert(t.enterValidAddress || 'Please fill in all required address fields');
      return;
    }

    setIsSaving(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Сохраняем адрес в localStorage
      localStorage.setItem('deliveryAddress', JSON.stringify(deliveryAddress));
      setAddressSaved(true);
      
    } catch (error) {
      console.error('Address save failed:', error);
      alert(t.deliveryFailed || 'Failed to save address. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!addressSaved) {
      alert(t.fillAllFields || 'Please save the delivery address first');
      return;
    }

    const completeOrder = {
      ...bookingData,
      deliveryAddress: {
        ...deliveryAddress,
        deliveryCost: deliveryCost
      },
      total: convertPrice(deliveryCost),
      orderDate: new Date().toISOString(),
      estimatedDelivery: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      orderId: orderId,
      paymentStatus: 'pending',
      orderStatus: 'confirmed'
    };
    
    localStorage.setItem('completeOrder', JSON.stringify(completeOrder));
    
    // Редирект на заглушку
    window.location.href = 'https://example.com';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const localeMap = {
      en: 'en-US',
      de: 'de-DE',
      fr: 'fr-FR',
      it: 'it-IT',
      es: 'es-ES',
      ru: 'ru-RU'
    };
    
    return date.toLocaleDateString(localeMap[language] || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!bookingData) {
    return (
      <div className="invoice-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t.loading || 'Loading booking information...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-page">
      <header className="header">
        <div className="container">
          <div className="logo">
            <span className="logo-icon">🏍️</span>
            <h1>ThaiBike Rent</h1>
          </div>
          <div className="header-right">
            <LanguageSwitcher />
            <CurrencySwitcher />
            <div className="contact-header">
              <a href="tel:+66123456789" className="phone">📞 +66 123 456 789</a>
              <span className="header-divider">|</span>
              <a href="mailto:info@thaibikerent.com" className="email">✉️ info@thaibikerent.com</a>
            </div>
          </div>
        </div>
      </header>

      <main className="invoice-main">
        <div className="container invoice-container">
          {/* Заголовок с номером заказа */}
          <div className="invoice-header">
            <div className="header-top">
              <button 
                className="back-button"
                onClick={() => navigate(-1)}
              >
                ← {t.backToBooking || 'Back'}
              </button>
              <div className="order-status">
                <span className="status-badge pending">⏳ {t.paymentPending || 'Payment Pending'}</span>
              </div>
            </div>
            
            <div className="invoice-title-section">
              <h1>💳 {t.invoiceTitle || 'Payment & Delivery'}</h1>
              <div className="invoice-meta">
                <div className="meta-item">
                  <span className="meta-label">{t.orderIdLabel || 'Order ID:'}</span>
                  <strong className="meta-value order-id">{orderId}</strong>
                </div>
                <div className="meta-item">
                  <span className="meta-label">{t.dateLabel || 'Date:'}</span>
                  <span className="meta-value">
                    {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 
                      language === 'de' ? 'de-DE' : 
                      language === 'fr' ? 'fr-FR' : 
                      language === 'it' ? 'it-IT' : 
                      language === 'es' ? 'es-ES' : 'en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Процесс оплаты */}
            <div className="payment-process">
              <h3>{t.paymentProcessTitle || 'Payment Process'}</h3>
              <div className="process-steps">
                <div className="process-step active">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <div className="step-title">{t.stepDelivery || 'Step 1: Delivery Fee'}</div>
                    <div className="step-desc">{t.paymentStep1 || 'Pay delivery fee'}: {formatPrice(deliveryCost)}</div>
                  </div>
                  <div className="step-status current">{t.currentStep || 'Current Step'}</div>
                </div>
                
                <div className="process-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <div className="step-title">{t.stepVerification || 'Step 2: Verification'}</div>
                    <div className="step-desc">{t.paymentStep2 || 'Document verification & contract signing'}</div>
                  </div>
                  <div className="step-status next">{t.nextStep || 'Next Step'}</div>
                </div>
                
                <div className="process-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <div className="step-title">{t.stepRental || 'Step 3: Rental Payment'}</div>
                    <div className="step-desc">{t.paymentStep3 || 'Pay bike rental fee - After contract'}: {formatPrice(bookingData.subtotal)}</div>
                  </div>
                  <div className="step-status">{t.bikePaymentReady || 'Bike payment ready'}</div>
                </div>
              </div>
              
              <div className="process-note">
                <div className="note-icon">ℹ️</div>
                <div className="note-content">
                  <strong>{t.twoStepPayment || 'Two-Step Payment Process:'}</strong>
                  <p>{t.paymentProcessNote || 'First pay delivery fee to reserve your bike. After verification and contract signing, you\'ll pay the rental fee separately.'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="invoice-layout">
            {/* Левая колонка - Информация о заказе */}
            <div className="invoice-left">
              {/* Информация о клиенте */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">👤</div>
                  <h2>{t.customerInfo || 'Customer Information'}</h2>
                </div>
                <div className="card-content">
                  <div className="info-row">
                    <span className="info-label">{t.fullName || 'Full Name'}:</span>
                    <span className="info-value">{bookingData.name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t.email || 'Email'}:</span>
                    <a href={`mailto:${bookingData.email}`} className="info-value link">{bookingData.email}</a>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t.phone || 'Phone'}:</span>
                    <a href={`tel:${bookingData.phone}`} className="info-value link">{bookingData.phone}</a>
                  </div>
                </div>
              </div>

              {/* Информация о байке */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-icon">🏍️</div>
                  <h2>{t.rentalDetails || 'Rental Details'}</h2>
                </div>
                <div className="card-content">
                  <div className="bike-summary">
                    <div className="bike-icon-large">🛵</div>
                    <div className="bike-info">
                      <h3>{bookingData.bike.name}</h3>
                      <div className="bike-features">
                        <span className="feature-tag">⛽ {bookingData.bike.engine || '125cc'}</span>
                        <span className="feature-tag">👥 {bookingData.bike.capacity || '2 Persons'}</span>
                        <span className="feature-tag">💰 {formatPrice(bookingData.bike.price)}/day</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rental-details-grid">
                    <div className="detail-card">
                      <div className="detail-icon">📅</div>
                      <div className="detail-content">
                        <div className="detail-label">{t.rentalPeriod || 'Rental Period'}</div>
                        <div className="detail-value">{formatDate(bookingData.startDate)} - {formatDate(bookingData.endDate)}</div>
                        <div className="detail-sub">({bookingData.days} {t.days?.toLowerCase() || 'days'})</div>
                      </div>
                    </div>
                    
                    <div className="detail-card">
                      <div className="detail-icon">📍</div>
                      <div className="detail-content">
                        <div className="detail-label">{t.pickupLocation || 'Pickup Location'}</div>
                        <div className="detail-value">{bookingData.location}</div>
                        <div className="detail-sub">10:00 AM</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="price-preview">
                    <div className="price-item">
                      <span>{t.dailyRate || 'Daily Rate'}:</span>
                      <span>{formatPrice(bookingData.bike.price)} × {bookingData.days} {t.days?.toLowerCase() || 'days'}</span>
                    </div>
                    <div className="price-item total">
                      <span>{t.subtotal || 'Subtotal'}:</span>
                      <span className="price-total">{formatPrice(bookingData.subtotal)}</span>
                    </div>
                    <div className="price-note">
                      <small>* {t.taxIncluded || 'All taxes included. No hidden fees.'}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Правая колонка - Оплата и доставка */}
            <div className="invoice-right">
              {/* Форма доставки */}
              <div className="delivery-card">
                <div className="card-header">
                  <div className="card-icon">🚚</div>
                  <div className="header-content">
                    <h2>{t.deliveryInformation || 'Delivery Information'}</h2>
                    <p className="card-subtitle">{t.completeToProceed || 'Complete to proceed with payment'}</p>
                  </div>
                </div>
                
                <div className="delivery-notice">
                  <div className="notice-icon">ℹ️</div>
                  <div className="notice-content">
                    <p><strong>{t.deliveryWithin60 || 'Delivery within 60 minutes'}</strong></p>
                    <p className="small">
                      {t.deliveryFeeNote || 'Standard delivery fee'}: {formatPrice(deliveryCost)} • {t.deliveryTime || 'Available 8:00 AM - 10:00 PM'}
                    </p>
                  </div>
                </div>

                <div className="address-form">
                  <div className="form-section-title">
                    <h3>{t.deliveryAddress || 'Delivery Address'}</h3>
                    <span className="required-hint">{t.requiredFields || '* Required fields'}</span>
                  </div>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="street">
                        {t.streetAddress || 'Street Address'}
                        {!deliveryAddress.street && <span className="error-indicator">!</span>}
                      </label>
                      <input
                        id="street"
                        type="text"
                        name="street"
                        value={deliveryAddress.street}
                        onChange={handleAddressChange}
                        placeholder={language === 'en' ? '123 Beach Road, Soi 5' : 
                                   language === 'de' ? 'Strandstraße 123, Soi 5' :
                                   language === 'fr' ? '123 Rue de la Plage, Soi 5' :
                                   language === 'it' ? 'Via della Spiaggia 123, Soi 5' :
                                   language === 'es' ? 'Calle Playa 123, Soi 5' :
                                   '123 Beach Road'}
                        className={deliveryAddress.street ? 'valid' : 'invalid'}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="city">
                        {t.city || 'City'}
                        {!deliveryAddress.city && <span className="error-indicator">!</span>}
                      </label>
                      <input
                        id="city"
                        type="text"
                        name="city"
                        value={deliveryAddress.city}
                        onChange={handleAddressChange}
                        placeholder="Patong"
                        className={deliveryAddress.city ? 'valid' : 'invalid'}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="district">
                        {t.district || 'District'}
                      </label>
                      <input
                        id="district"
                        type="text"
                        name="district"
                        value={deliveryAddress.district}
                        onChange={handleAddressChange}
                        placeholder="Kathu"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="province">
                        {t.province || 'Province'}
                        {!deliveryAddress.province && <span className="error-indicator">!</span>}
                      </label>
                      <input
                        id="province"
                        type="text"
                        name="province"
                        value={deliveryAddress.province}
                        onChange={handleAddressChange}
                        placeholder="Phuket"
                        className={deliveryAddress.province ? 'valid' : 'invalid'}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="postalCode">
                        {t.postalCode || 'Postal Code'}
                        {!deliveryAddress.postalCode && <span className="error-indicator">!</span>}
                      </label>
                      <input
                        id="postalCode"
                        type="text"
                        name="postalCode"
                        value={deliveryAddress.postalCode}
                        onChange={handleAddressChange}
                        placeholder="83150"
                        className={deliveryAddress.postalCode ? 'valid' : 'invalid'}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="country">
                        {t.country || 'Country'}
                      </label>
                      <input
                        id="country"
                        type="text"
                        name="country"
                        value={deliveryAddress.country}
                        onChange={handleAddressChange}
                        placeholder="Thailand"
                        readOnly
                        className="readonly"
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label htmlFor="notes">
                        {t.deliveryNotesOptional || 'Delivery Notes (Optional)'}
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={deliveryAddress.notes}
                        onChange={handleAddressChange}
                        placeholder={language === 'en' ? 'Gate code, building name, special instructions...' :
                                   language === 'de' ? 'Torkode, Gebäudename, besondere Anweisungen...' :
                                   language === 'fr' ? 'Code de porte, nom du bâtiment, instructions spéciales...' :
                                   language === 'it' ? 'Codice cancello, nome edificio, istruzioni speciali...' :
                                   language === 'es' ? 'Código de puerta, nombre del edificio, instrucciones especiales...' :
                                   'Special instructions'}
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    {!addressSaved ? (
                      <button 
                        type="button" 
                        className={`btn-save-address ${formValid ? 'active' : 'disabled'}`}
                        onClick={saveAddress}
                        disabled={!formValid || isSaving}
                      >
                        {isSaving ? (
                          <>
                            <span className="spinner-small"></span>
                            {t.saving || 'Saving...'}
                          </>
                        ) : (
                          <>
                            <span className="save-icon">💾</span>
                            {t.saveAddress || 'Save Delivery Address'}
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="success-message">
                        <div className="success-icon">✅</div>
                        <div className="success-content">
                          <strong>{t.addressSavedSuccess || 'Address Saved Successfully!'}</strong>
                          <p>{t.deliveryConfirmation || 'Delivery will be made to this address within 60 minutes.'}</p>
                        </div>
                      </div>
                    )}
                    
                    {!formValid && !addressSaved && (
                      <div className="validation-message">
                        <span className="warning-icon">⚠️</span>
                        {t.fillRequiredFields || 'Please fill all required fields marked with *'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Оплата */}
              <div className="payment-card">
                <div className="card-header">
                  <div className="card-icon">💰</div>
                  <div className="header-content">
                    <h2>{t.paymentSummary || 'Payment Summary'}</h2>
                    <p className="card-subtitle">{t.cardPaymentOnly || 'Card payment only'}</p>
                  </div>
                </div>
                
                <div className="payment-summary">
                  <div className="summary-row">
                    <span className="summary-label">{t.deliveryFee || 'Delivery Fee:'}</span>
                    <span className="summary-value">{addressSaved ? formatPrice(deliveryCost) : '—'}</span>
                  </div>
                  
                  <div className="summary-row">
                    <span className="summary-label">{t.rentalAmount || 'Rental Amount:'}</span>
                    <span className="summary-value">{t.paidSeparately || 'Paid Separately'}</span>
                  </div>
                  
                  <div className="summary-divider"></div>
                  
                  <div className="summary-row total">
                    <span className="total-label">{t.totalToPay || 'Total to Pay:'}</span>
                    <span className="total-amount">{addressSaved ? formatPrice(deliveryCost) : '—'}</span>
                  </div>
                  
                  <div className="payment-note">
                    <div className="note-icon">ℹ️</div>
                    <div className="note-text">
                      {t.rentalPaidSeparately || 'Rental amount will be paid separately after contract signing.'}
                    </div>
                  </div>
                </div>

                <div className="payment-method">
                  <div className="method-header">
                    <div className="method-icon">💳</div>
                    <div className="method-info">
                      <h3>{t.creditDebitCard || 'Credit / Debit Card'}</h3>
                      <p>{t.cardTypes || 'Visa, MasterCard, American Express'}</p>
                    </div>
                  </div>
                  <div className="method-security">
                    <span className="security-badge">🔒 {t.securePayment || 'SSL Secure'}</span>
                    <span className="security-badge">✅ 3D Secure</span>
                  </div>
                </div>

                <button 
                  className={`btn-pay ${addressSaved ? 'active' : 'disabled'}`}
                  onClick={handleProceedToPayment}
                  disabled={!addressSaved}
                >
                  <span className="pay-icon">💳</span>
                  <span className="pay-text">
                    {t.paymentComplete || 'Complete Payment'}
                    {addressSaved && <span className="pay-amount">{formatPriceOnly(deliveryCost)}</span>}
                  </span>
                  <span className="pay-arrow">→</span>
                </button>
                
                <div className="payment-security-note">
                  <span className="lock-icon">🔒</span>
                  <span>{t.secureNote || 'Your payment is secure and encrypted. We don\'t store card details.'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Информация о поддержке */}
          <div className="support-info">
            <div className="support-card">
              <div className="support-icon">📞</div>
              <div className="support-content">
                <h3>{t.needHelp || 'Need Help?'}</h3>
                <p>{t.support24_7 || 'Our support team is available 24/7 to assist you.'}</p>
                <div className="support-contacts">
                  <a href="tel:+66123456789" className="contact-link">📞 +66 123 456 789</a>
                  <a href="https://wa.me/66123456789" className="contact-link">💬 {t.contactWhatsApp || 'WhatsApp'}</a>
                  <a href="mailto:support@thaibikerent.com" className="contact-link">✉️ {t.contactEmail || 'Email Support'}</a>
                </div>
              </div>
            </div>
            
            <div className="support-card">
              <div className="support-icon">💱</div>
              <div className="support-content">
                <h3>{t.currencyInfoTitle || 'Currency Information'}</h3>
                <ul className="info-list">
                  <li>✅ {t.currencyInfo1 || 'All prices are converted at checkout'}</li>
                  <li>✅ {t.currencyInfo2 || 'No hidden fees or conversion charges'}</li>
                  <li>✅ {t.currencyInfo3 || 'Payment processed in selected currency'}</li>
                  <li>✅ {t.currencyInfo4 || 'Exchange rates updated daily'}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InvoicePage;